import { Box, Divider, Paper, Stack, SvgIcon, Typography, useTheme } from '@mui/material'
import { ChartsTooltipContainer, useAxesTooltip } from '@mui/x-charts'
import * as R from 'remeda'
import { formatSalesValue, getChartColors, THIS_YEAR_SERIES } from '../utils'
import { IconShoppingBag, IconUser } from '@tabler/icons-react'

type BreakdownEntry = { label: string; count: number; total: number }

type MonthBreakdown = {
  count: number
  byAgency: BreakdownEntry[]
  byCountry: BreakdownEntry[]
}

export type MonthlyBreakdown = {
  lastYear: MonthBreakdown[]
  thisYear: MonthBreakdown[]
}

const BreakdownSection = ({ title, entries }: { title: string; entries: BreakdownEntry[] }) => (
  <Stack gap={0.5} sx={{ flex: 1, minWidth: 200 }}>
    <Typography variant="caption" fontWeight={600} color="text.secondary">
      {title}
    </Typography>
    {entries.slice(0, 5).map(({ label, count, total }) => (
      <Stack key={label} direction="row" justifyContent="space-between" gap={2}>
        <Typography variant="caption" whiteSpace="nowrap">
          {label}
        </Typography>
        <Stack direction="row" spacing={0.5} useFlexGap>
          <Stack
            direction="row"
            sx={{ justifyContent: 'center', alignItems: 'center' }}
            spacing={0.5}
          >
            <SvgIcon sx={{ width: 12, height: 12, color: (t) => t.palette.text.disabled }}>
              <IconShoppingBag />
            </SvgIcon>
            <Typography variant="caption" fontWeight={500}>
              {formatSalesValue(total)}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            sx={{ justifyContent: 'center', alignItems: 'center' }}
            spacing={0.5}
          >
            <SvgIcon sx={{ width: 12, height: 12, color: (t) => t.palette.text.disabled }}>
              <IconUser />
            </SvgIcon>
            <Typography variant="caption" fontWeight={500}>
              {count}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    ))}
  </Stack>
)

const TooltipContent = ({ lastYear, thisYear }: MonthlyBreakdown) => {
  const tooltipData = useAxesTooltip()
  const theme = useTheme()
  if (!tooltipData) return null

  const axisTooltip = tooltipData[0]
  if (!axisTooltip) return null

  const { dataIndex, axisFormattedValue, seriesItems } = axisTooltip
  const lastYearBreakdown = lastYear[dataIndex]
  const thisYearBreakdown = thisYear[dataIndex]

  if (!lastYearBreakdown || !thisYearBreakdown) return null

  const columns = [
    { item: seriesItems[0], breakdown: lastYearBreakdown },
    { item: seriesItems[1], breakdown: thisYearBreakdown },
  ]
  const { primaryColor, secondaryColor } = getChartColors(theme)
  return (
    <Paper elevation={3} sx={{ p: 1.5, minWidth: 320 }}>
      <Typography variant="subtitle2" fontWeight={700} mb={1}>
        {axisFormattedValue}
      </Typography>
      <Stack
        direction="row"
        spacing={2}
        divider={<Divider orientation="vertical" flexItem />}
        useFlexGap
      >
        {columns.map(({ item, breakdown }) => {
          if (!item) return null
          if (breakdown.byAgency.length === 0 && breakdown.byCountry.length === 0) return null
          return (
            <Stack key={item.seriesId.toString()} flex={1} spacing={1} useFlexGap>
              <Stack direction="row" alignItems="center" spacing={0.75} useFlexGap>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor:
                      item.seriesId === THIS_YEAR_SERIES ? primaryColor : secondaryColor,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="subtitle2" fontWeight={700}>
                  {item.formattedLabel}
                </Typography>
                <Stack direction="row" spacing={0.5} useFlexGap sx={{ ml: 'auto' }}>
                  <Stack direction="row" alignItems="center" spacing={0.5} useFlexGap>
                    <SvgIcon sx={{ width: 12, height: 12, color: (t) => t.palette.text.disabled }}>
                      <IconShoppingBag />
                    </SvgIcon>
                    <Typography variant="subtitle2">{item.formattedValue}</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.5} useFlexGap>
                    <SvgIcon sx={{ width: 12, height: 12, color: (t) => t.palette.text.disabled }}>
                      <IconUser />
                    </SvgIcon>
                    <Typography variant="subtitle2">{breakdown.count}</Typography>
                  </Stack>
                </Stack>
              </Stack>
              <Divider />
              <BreakdownSection
                title="By Agency"
                entries={breakdown.byAgency.map((bd) => ({
                  ...bd,
                  label:
                    R.pipe(bd.label, R.split('-'), R.first(), (label) => label?.trim()) ?? bd.label,
                }))}
              />
              <Divider />
              <BreakdownSection title="By Country" entries={breakdown.byCountry} />
            </Stack>
          )
        })}
      </Stack>
    </Paper>
  )
}

export const CustomTooltip = (props: MonthlyBreakdown) => (
  <ChartsTooltipContainer trigger="axis">
    <TooltipContent {...props} />
  </ChartsTooltipContainer>
)
